import React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CasinoOutlined as CasinoIcon,
  ScheduleOutlined as ScheduleIcon,
  CheckCircle as VerifiedIcon,
  AutoAwesomeOutlined as SparkleIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  Path,
  STATUS_CONFIG,
  getLotteryTypeName,
  getLotteryTypeConfig,
  DEFAULT_CURRENCY,
} from "../../../../../static/Constants";
import { CommonChip, CommonBreadcrumbs } from "../../../../Components/ui";

const LotteryHeader = ({
  lotteryId,
  lottery,
  datelist,
  partnerDetails,
  partnerLogoUrl,
  currency = DEFAULT_CURRENCY,
  totalPrizeFormatted = "0",
}) => {
  const history = useHistory();

  const typeId = Number(lottery?.lotteryType !== undefined ? lottery.lotteryType : 2);
  const statusNum = lottery?.status !== undefined ? Number(lottery.status) : 0;
  const statusInfo = STATUS_CONFIG[statusNum] || STATUS_CONFIG[0];
  const typeConfig = getLotteryTypeConfig(typeId);
  const TypeIcon = typeConfig.icon || CasinoIcon;
  const typeName = typeConfig.name || getLotteryTypeName(typeId);
  const partnerName =
    partnerDetails?.name || partnerDetails?.companyName || "Creator";

  return (
    <Box sx={{ mb: 4 }}>
      {/* Top Navigation & Breadcrumbs */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title="Back to All Lotteries">
            <IconButton
              onClick={() => history.push(Path.allLotteries || "/all-lotteries")}
              sx={{
                bgcolor: colors.slate[100],
                color: colors.slate[700],
                border: `1px solid ${colors.slate[200]}`,
                p: 1,
                "&:hover": {
                  bgcolor: colors.slate[200],
                  color: colors.primary.main,
                },
              }}
              size="small"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <CommonBreadcrumbs
            sx={{ mb: 0 }}
            items={[
              { label: "Home", path: Path.root },
              { label: "All Lotteries", path: Path.allLotteries || "/all-lotteries" },
              { label: `${typeName} #${lotteryId}` },
            ]}
          />
        </Stack>

        {/* Creator / Partner Tag */}
        {partnerDetails && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              bgcolor: "#ffffff",
              border: `1px solid ${colors.slate[200]}`,
              borderRadius: borderRadiusPx.full || "9999px",
              py: 0.5,
              px: 1.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            <Avatar
              src={partnerLogoUrl}
              alt={partnerName}
              sx={{ width: 24, height: 24, bgcolor: colors.primary.lighter }}
            >
              {partnerName.charAt(0)}
            </Avatar>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: colors.slate[700],
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {partnerName}
            </Typography>
            <VerifiedIcon sx={{ fontSize: 15, color: colors.primary.main }} />
          </Stack>
        )}
      </Stack>

      {/* Hero Banner Card */}
      <Box
        className="buylottery-hero-banner"
        sx={{
          p: { xs: 3, md: 4 },
          boxShadow: customShadows.card || "0 4px 20px rgba(0,0,0,0.05)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
        >
          {/* Left: Title & Prize Callout */}
          <Box>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <CommonChip
                label={typeName}
                icon={<TypeIcon sx={{ fontSize: 16, color: `${typeConfig.color} !important` }} />}
                sx={{
                  bgcolor: typeConfig.bgColor,
                  color: typeConfig.color,
                  border: `1px solid ${typeConfig.borderColor || `${typeConfig.color}30`}`,
                  fontWeight: 700,
                }}
              />
              <CommonChip
                label={statusInfo.label}
                variant={statusInfo.variant || "active"}
              />
              {typeId === 1 ? (
                <CommonChip
                  label={`Pool: ${lottery?.capacity || 20} Mine Tickets`}
                  variant="filterInactive"
                />
              ) : typeId === 3 ? (
                <CommonChip
                  label={`Level ${datelist?.level || 4} Rocket`}
                  variant="filterInactive"
                />
              ) : typeId === 0 ? (
                <CommonChip
                  label="Instant Spin Wheel"
                  variant="filterInactive"
                />
              ) : (
                lottery?.pickNumbers &&
                lottery?.capacity && (
                  <CommonChip
                    label={`Pick ${lottery.pickNumbers} of ${lottery.capacity}`}
                    variant="filterInactive"
                  />
                )
              )}
            </Stack>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: colors.slate[500],
                textTransform: "uppercase",
                letterSpacing: 1,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                mt: 1.5,
              }}
            >
              <SparkleIcon sx={{ fontSize: 18, color: colors.amber[500] }} />
              Chance to Win Grand Prize
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2.2rem", sm: "3rem", md: "3.5rem" },
                lineHeight: 1.1,
                mt: 0.5,
                fontWeight: 800,
                background: typeConfig.gradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {totalPrizeFormatted} {currency}
            </Typography>
          </Box>

          {/* Right: Date / Schedule Info Card */}
          <Box
            sx={{
              bgcolor: "#ffffff",
              borderRadius: borderRadiusPx.lg || "16px",
              p: 2.5,
              border: `1px solid ${colors.slate[200]}`,
              minWidth: { xs: "100%", sm: 260, md: 300 },
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ScheduleIcon sx={{ fontSize: 18, color: colors.primary.main }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.slate[800] }}>
                  Draw Schedule
                </Typography>
              </Stack>

              <Box sx={{ borderTop: `1px solid ${colors.slate[100]}`, pt: 1 }}>
                <Typography variant="caption" sx={{ color: colors.slate[400], display: "block" }}>
                  Draw Cut-off Date:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[700] }}>
                  {datelist?.endTime && Number(datelist.endTime) > 0
                    ? moment.unix(datelist.endTime).format("DD MMM YYYY, hh:mm A")
                    : "Rolling On-Chain Draw"}
                </Typography>
              </Box>

              {datelist?.drawTime && Number(datelist.drawTime) > 0 && (
                <Box sx={{ borderTop: `1px solid ${colors.slate[100]}`, pt: 1 }}>
                  <Typography variant="caption" sx={{ color: colors.slate[400], display: "block" }}>
                    Expected Results:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.emerald[600] }}>
                    {moment.unix(datelist.drawTime).format("DD MMM YYYY, hh:mm A")}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default LotteryHeader;
